import React, { useState } from 'react';
import { FaFlask } from 'react-icons/fa';
import { FiInfo, FiRefreshCw } from 'react-icons/fi';
import { PageHeader } from '../components/ui';
import {
  calculateAgingAddition,
  calculateCrushAddition,
  CalculatorMode,
  formatSo2Amount,
  KMBS_SO2_YIELD,
  MOLECULAR_SO2_OPTIONS,
  SO2_PKA,
  SO2_SOURCES,
  So2SourceId,
  VolumeUnit,
} from '../utils/kmbsFormula';

const MODE_OPTIONS: { value: CalculatorMode; label: string; hint: string }[] = [
  { value: 'crush', label: 'Crush', hint: 'Pre-ferment · static Free SO₂' },
  { value: 'aging', label: 'Aging', hint: 'pH-based molecular SO₂' },
];

const SOURCE_OPTIONS = (Object.keys(SO2_SOURCES) as So2SourceId[]).map((id) => SO2_SOURCES[id]);

const CRUSH_EXAMPLE = {
  volume: '225',
  volumeUnit: 'liters' as VolumeUnit,
  currentFreeSo2: '0',
  targetFreeSo2: '40',
  source: 'kmbs' as So2SourceId,
};

const AGING_EXAMPLE = {
  volume: '225',
  volumeUnit: 'liters' as VolumeUnit,
  currentFreeSo2: '20',
  pH: '3.50',
  molecularSo2: 0.8,
  source: 'kmbs' as So2SourceId,
};

const KmbsCalculator: React.FC = () => {
  const [mode, setMode] = useState<CalculatorMode>('crush');
  const [volume, setVolume] = useState('');
  const [volumeUnit, setVolumeUnit] = useState<VolumeUnit>('liters');
  const [currentFreeSo2, setCurrentFreeSo2] = useState('0');
  const [targetFreeSo2, setTargetFreeSo2] = useState('');
  const [pH, setPH] = useState('');
  const [molecularSo2, setMolecularSo2] = useState(0.5);
  const [source, setSource] = useState<So2SourceId>('kmbs');

  const volumeNum = volume === '' ? NaN : Number(volume);
  const currentNum = currentFreeSo2 === '' ? NaN : Number(currentFreeSo2);
  const targetNum = targetFreeSo2 === '' ? NaN : Number(targetFreeSo2);
  const pHNum = pH === '' ? NaN : Number(pH);

  const result =
    mode === 'crush'
      ? calculateCrushAddition({
          volume: volumeNum,
          volumeUnit,
          currentFreeSo2: currentNum,
          targetFreeSo2: targetNum,
          source,
        })
      : calculateAgingAddition({
          volume: volumeNum,
          volumeUnit,
          currentFreeSo2: currentNum,
          pH: pHNum,
          molecularSo2,
          source,
        });

  const sourceMeta = SO2_SOURCES[source];
  const amountDisplay = formatSo2Amount(result.amount);
  const targetFreeDisplay =
    result.targetFreeSo2 !== null && Number.isFinite(result.targetFreeSo2)
      ? result.targetFreeSo2.toFixed(1)
      : null;

  const handleLoadExample = () => {
    if (mode === 'crush') {
      setVolume(CRUSH_EXAMPLE.volume);
      setVolumeUnit(CRUSH_EXAMPLE.volumeUnit);
      setCurrentFreeSo2(CRUSH_EXAMPLE.currentFreeSo2);
      setTargetFreeSo2(CRUSH_EXAMPLE.targetFreeSo2);
      setSource(CRUSH_EXAMPLE.source);
    } else {
      setVolume(AGING_EXAMPLE.volume);
      setVolumeUnit(AGING_EXAMPLE.volumeUnit);
      setCurrentFreeSo2(AGING_EXAMPLE.currentFreeSo2);
      setPH(AGING_EXAMPLE.pH);
      setMolecularSo2(AGING_EXAMPLE.molecularSo2);
      setSource(AGING_EXAMPLE.source);
    }
  };

  const handleReset = () => {
    setVolume('');
    setVolumeUnit('liters');
    setCurrentFreeSo2('0');
    setTargetFreeSo2('');
    setPH('');
    setMolecularSo2(0.5);
    setSource('kmbs');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="KMBS Calculator"
        subtitle="How much SO₂ to add at crush or during aging"
        icon={FaFlask({ className: 'h-5 w-5' })}
      />

      <div className="inline-flex rounded-md border border-monday-border overflow-hidden text-sm">
        {MODE_OPTIONS.map((option, index) => {
          const selected = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => setMode(option.value)}
              className={`px-4 py-2.5 text-left transition-colors ${
                index > 0 ? 'border-l border-monday-border' : ''
              } ${
                selected
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-monday-text hover:bg-monday-hover'
              }`}
            >
              <div className="font-medium">{option.label}</div>
              <div className={`text-xs mt-0.5 ${selected ? 'text-white/80' : 'opacity-70'}`}>
                {option.hint}
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        <div className="lg:col-span-3 bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-monday-border">
            <h3 className="text-lg font-medium text-monday-text">Addition inputs</h3>
            <p className="mt-1 text-sm text-monday-text-secondary">
              {mode === 'crush'
                ? 'Static Free SO₂ target for must or juice at crush.'
                : 'Molecular SO₂ target drives Free SO₂ from wine pH.'}
            </p>
          </div>

          <div className="px-4 py-5 sm:p-6 space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="volume" className="form-label mb-0">
                  {mode === 'crush' ? 'Must / juice volume' : 'Wine volume'}
                </label>
                <div className="inline-flex rounded-md border border-monday-border overflow-hidden text-xs">
                  <button
                    type="button"
                    onClick={() => setVolumeUnit('liters')}
                    className={`px-3 py-1.5 font-medium transition-colors ${
                      volumeUnit === 'liters'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-monday-text hover:bg-monday-hover'
                    }`}
                  >
                    Liters
                  </button>
                  <button
                    type="button"
                    onClick={() => setVolumeUnit('gallons')}
                    className={`px-3 py-1.5 font-medium transition-colors border-l border-monday-border ${
                      volumeUnit === 'gallons'
                        ? 'bg-primary-500 text-white'
                        : 'bg-white text-monday-text hover:bg-monday-hover'
                    }`}
                  >
                    US gallons
                  </button>
                </div>
              </div>
              <input
                id="volume"
                type="number"
                min="0"
                step="0.1"
                inputMode="decimal"
                value={volume}
                onChange={(e) => setVolume(e.target.value)}
                className="mobile-form-input"
                placeholder={volumeUnit === 'liters' ? 'e.g. 225' : 'e.g. 59.44'}
              />
              {volumeUnit === 'gallons' && (
                <p className="mt-1 text-xs text-monday-text-secondary">
                  Converted at 1 gal = 3.78541 L
                  {result.volumeLiters != null && Number.isFinite(result.volumeLiters)
                    ? ` → ${result.volumeLiters.toFixed(2)} L`
                    : ''}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="currentFreeSo2" className="form-label mb-1">
                Current Free SO₂
              </label>
              <div className="relative">
                <input
                  id="currentFreeSo2"
                  type="number"
                  min="0"
                  step="0.1"
                  inputMode="decimal"
                  value={currentFreeSo2}
                  onChange={(e) => setCurrentFreeSo2(e.target.value)}
                  className="mobile-form-input pr-14"
                  placeholder={mode === 'crush' ? '0 for fresh grapes' : 'e.g. 20'}
                />
                <span className="absolute inset-y-0 right-3 flex items-center text-sm text-monday-text-secondary pointer-events-none">
                  ppm
                </span>
              </div>
            </div>

            {mode === 'crush' ? (
              <div>
                <label htmlFor="targetFreeSo2" className="form-label mb-1">
                  Target Free SO₂
                </label>
                <div className="relative">
                  <input
                    id="targetFreeSo2"
                    type="number"
                    min="0"
                    step="0.1"
                    inputMode="decimal"
                    value={targetFreeSo2}
                    onChange={(e) => setTargetFreeSo2(e.target.value)}
                    className="mobile-form-input pr-14"
                    placeholder="usually 30–50"
                  />
                  <span className="absolute inset-y-0 right-3 flex items-center text-sm text-monday-text-secondary pointer-events-none">
                    ppm
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <label htmlFor="winePh" className="form-label mb-1">
                    Wine pH
                  </label>
                  <input
                    id="winePh"
                    type="number"
                    min="0"
                    max="14"
                    step="0.01"
                    inputMode="decimal"
                    value={pH}
                    onChange={(e) => setPH(e.target.value)}
                    className="mobile-form-input"
                    placeholder="e.g. 3.50"
                  />
                </div>
                <div>
                  <label htmlFor="molecularSo2" className="form-label mb-1">
                    Target Molecular SO₂
                  </label>
                  <select
                    id="molecularSo2"
                    value={molecularSo2}
                    onChange={(e) => setMolecularSo2(Number(e.target.value))}
                    className="mobile-form-input"
                  >
                    {MOLECULAR_SO2_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div>
              <label htmlFor="so2Source" className="form-label mb-1">
                SO₂ source
              </label>
              <select
                id="so2Source"
                value={source}
                onChange={(e) => setSource(e.target.value as So2SourceId)}
                className="mobile-form-input"
              >
                {SOURCE_OPTIONS.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label} ({(option.yield * 100).toFixed(option.yield >= 0.1 ? 1 : 0)}%)
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                onClick={handleLoadExample}
                className="inline-flex items-center px-3 py-2 border border-monday-border shadow-sm text-sm font-medium rounded-md text-monday-text bg-white hover:bg-monday-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200"
              >
                {mode === 'crush'
                  ? 'Try example (225 L · 0 → 40 ppm)'
                  : 'Try example (225 L · pH 3.5 · 0.8)'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center px-3 py-2 border border-monday-border shadow-sm text-sm font-medium rounded-md text-monday-text bg-white hover:bg-monday-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200"
              >
                {FiRefreshCw({ className: 'mr-1.5 h-4 w-4' })}
                Reset
              </button>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg h-full">
            <div className="px-4 py-5 sm:px-6 border-b border-monday-border">
              <h3 className="text-lg font-medium text-monday-text">
                {sourceMeta.unit === 'g' ? 'KMBS to weigh' : 'Liquid SO₂ to add'}
              </h3>
            </div>
            <div className="px-4 py-8 sm:p-8 text-center">
              <div
                className="text-5xl font-semibold tracking-tight text-monday-text tabular-nums"
                aria-live="polite"
              >
                {amountDisplay}
                {result.amount !== null && (
                  <span className="ml-2 text-2xl font-medium text-monday-text-secondary">
                    {result.unit}
                  </span>
                )}
              </div>
              <p className="mt-2 text-sm text-monday-text-secondary">{sourceMeta.label}</p>

              {result.noAdditionRequired && (
                <p className="mt-4 text-sm font-medium text-primary-700">No addition required.</p>
              )}

              {mode === 'aging' && targetFreeDisplay !== null && (
                <p className="mt-4 text-sm text-monday-text">
                  Target Free SO₂:{' '}
                  <span className="font-medium tabular-nums">{targetFreeDisplay} ppm</span>
                </p>
              )}

              <p className="mt-6 text-sm text-monday-text-secondary leading-relaxed">
                {sourceMeta.unit === 'g'
                  ? 'Dissolve in a little warm water or wine, then mix thoroughly into the vessel.'
                  : 'Add liquid SO₂ carefully and mix thoroughly; re-test Free SO₂ after equilibration.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-monday-border rounded-lg px-4 py-3 sm:px-5 flex gap-3">
        <div className="flex-shrink-0 mt-0.5 text-primary-500">
          {FiInfo({ className: 'h-5 w-5' })}
        </div>
        <div className="text-sm text-monday-text-secondary space-y-1">
          <p>
            <span className="text-monday-text font-medium">Crush:</span> use a static Target Free
            SO₂. Addition = V × (T − C) / (1000 × Y).
          </p>
          <p>
            <span className="text-monday-text font-medium">Aging:</span> Target Free SO₂ from
            molecular SO₂ and pH (pKa {SO2_PKA}): T = M × (1 + 10^(pH − {SO2_PKA})), then the same
            addition formula.
          </p>
          <p>
            Default KMBS yield is{' '}
            <span className="text-monday-text font-medium">
              {(KMBS_SO2_YIELD * 100).toFixed(1)}%
            </span>
            ; liquid sources use 10% or 6% and report mL instead of grams. Only Free SO₂ protects
            the wine — re-test a few days after the addition.
          </p>
        </div>
      </div>
    </div>
  );
};

export default KmbsCalculator;
